import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import TenantApi from '../../api/tenant/tenant-api-factory';
import { FileApi } from '../../api/file/file-api';
import InventoryModal from '../../components/tenants/inventory-modal';
import { useMsal } from '@azure/msal-react';

export const InventoryDetail = () => {

    const navigate = useNavigate();

    const { id, inventoryId } = useParams();
    const [searchParams] = useSearchParams();
    
    const { instance, inProgress, accounts } = useMsal();

    const [tenant, setTenant] = useState(null);
    const [inventory, setInventory] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedTenancyIndex, setSelectedTenancyIndex] = useState(0);

    const tenantApi = useMemo(() => new TenantApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
    const fileApi = useMemo(() => new FileApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    useEffect(() => {
        
        let mounted = true;

        async function load() {
            setLoading(true);
            try {
                const t = await tenantApi.get(id);
                if (!mounted) return;
                setTenant(t);
                if (inventoryId === 'new') {
                    const tenancyIndex = parseInt(searchParams.get('tenancyIndex')) || 0;
                    const type = searchParams.get('type') || 'periodic';
                    setInventory({ id: null, type, date: new Date().toISOString(), notes: '', photos: [] });
                    setSelectedTenancyIndex(tenancyIndex);
                } else {
                    const inv = (t.tenancies || []).flatMap(x => x.inventories || []).find(i => i.id === inventoryId);
                    setInventory(inv ?? null);
                }
            } catch (err) {
                console.error('InventoryDetail: Failed to load tenant or inventory', err);
            } finally {
                if (mounted) setLoading(false);
            }
        }
        load();

        return () => { mounted = false; };
    }, [id, inventoryId, instance, inProgress, accounts, searchParams, tenantApi]);

    if (loading) return <div>Loading...</div>;

    if (!tenant) return <div>Tenant not found</div>;
    if (!inventory) return <div>Inventory not found</div>;

    const tenancy = inventoryId === 'new' ? tenant.tenancies[selectedTenancyIndex] : tenant.tenancies.find(t => (t.inventories||[]).some(i => i.id === inventoryId));

    const saveInventory = async (updatedTenant, savedInventory) => {
        try {
            // Upload any photo files included on the savedInventory before persisting
            try {
                const companyName = updatedTenant?.company?.name || tenant?.company?.name;
                if (companyName && savedInventory && Array.isArray(savedInventory.photos)) {
                    const photosWithFiles = savedInventory.photos.filter(p => p && p.file);
                    if (photosWithFiles.length > 0) {
                        const collection = photosWithFiles.map(p => p.file);
                        const pathBase = `${updatedTenant.id || tenant.id}/Inventories/${savedInventory.id}`;
                        setUploadProgress(collection.map(c => ({ name: c.name, remainingBytes: c.size, originalSize: c.size })));
                        const uploadResponses = await fileApi.uploadFiles(companyName, pathBase, collection, (details) => {
                            // Ensure each detail entry has originalSize to compute percent
                            const withOriginal = (details || []).map(d => ({ originalSize: d.originalSize || (d.remainingBytes || 0), name: d.name, remainingBytes: d.remainingBytes }));
                            setUploadProgress(withOriginal);
                        });

                        // Map upload responses back to photos in updatedTenant
                        for (const resp of uploadResponses) {
                            try {
                                const fileName = resp.file && resp.file.name;
                                // Find photo entries matching file name in the updatedTenant
                                for (const t of updatedTenant.tenancies || []) {
                                    for (const inv of t.inventories || []) {
                                        if (inv.id === savedInventory.id) {
                                            for (const photo of inv.photos || []) {
                                                if (photo.file && photo.file.name === fileName) {
                                                    // Persist returned path and SAS-tokened urls so the UI
                                                    // can show blob-hosted images immediately.
                                                    photo.path = resp.path || photo.path;
                                                    if (resp.pathIncSASToken) photo.pathIncSASToken = resp.pathIncSASToken;
                                                    if (resp.thumbPathIncSASToken) photo.thumbPathIncSASToken = resp.thumbPathIncSASToken;
                                                }
                                            }
                                        }
                                    }
                                }
                            } catch (e) {
                                console.warn('Error mapping uploaded file response', e);
                            }
                        }

                        // Remove file objects to avoid sending binary data in the JSON payload
                        for (const t of updatedTenant.tenancies || []) {
                            for (const inv of t.inventories || []) {
                                if (inv.id === savedInventory.id) {
                                    for (const photo of inv.photos || []) {
                                        if (photo.file) delete photo.file;
                                    }
                                }
                            }
                        }
                        // Clear upload progress after mapping
                        setUploadProgress([]);
                    }
                }
            } catch (uploadErr) {
                console.error('Error uploading inventory photos', uploadErr);
                // Continue and try to save tenant anyway (photos may have failed)
            }

            setIsSaving(true);
            const result = await tenantApi.update(updatedTenant);
            setTenant(result);
            setIsSaving(false);

            // If the modal supplied the savedInventory (created/edited), prefer that
            // but ensure we pick the persisted version from the returned tenant.
            let inv = null;
            if (savedInventory && savedInventory.id) {
                inv = (result.tenancies || []).flatMap(x => x.inventories || []).find(i => i.id === savedInventory.id);
            }
            if (!inv) {
                inv = (result.tenancies || []).flatMap(x => x.inventories || []).find(i => i.id === inventoryId || i.id === (savedInventory && savedInventory.id));
            }

            // If the update result doesn't contain the inventory we just saved,
            // attempt a fresh GET to ensure the server persisted it and to refresh
            // the cache/state in case the API returns a different shape.
            if (!inv) {
                try {
                    console.log('InventoryDetail: saved inventory not found in update result, reloading tenant from server');
                    const reloaded = await tenantApi.get(id);
                    setTenant(reloaded);
                    inv = (reloaded.tenancies || []).flatMap(x => x.inventories || []).find(i => i.id === (savedInventory && savedInventory.id));
                } catch (e) {
                    console.warn('InventoryDetail: failed to reload tenant after update', e);
                }
            }

            setInventory(inv ?? null);
            // After successful save and state refresh, navigate back to the previous page
            try {
                // If there's history, go back one; otherwise navigate to the tenant tenancies list
                if (window.history.length > 1) navigate(-1); else navigate(`/tenant/detail/${id}/tenancies`);
            } catch (e) {
                // non-fatal: if navigation fails, keep user on the page with updated state
                console.warn('InventoryDetail: navigation after save failed', e);
            }
        } catch (err) {
            console.error('Failed to save inventory', err);
        }
    };

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>{/* debug banner removed */}</div>
                <div>
                    <button className="btn btn-sm btn-secondary me-2" onClick={() => window.history.length > 1 ? window.history.back() : null}>Back</button>
                    <Link className="btn btn-sm btn-outline-primary" to={`/tenant/detail/${id}/tenancies`}>Back to tenant</Link>
                </div>
            </div>
            <h3>Inventory Detail</h3>
            <div className="mb-2">Tenant: {tenant.displayName}</div>
                                    <InventoryModal asPage={true} show={true} cancel={() => navigate(-1)} tenancy={tenancy} tenancyIndex={selectedTenancyIndex} tenant={tenant} setTenant={setTenant} inventory={inventory} onSave={saveInventory} uploadProgress={uploadProgress} savingInProgress={isSaving} />
        </div>
    );
};

export default InventoryDetail;
